import * as React from 'react';
import { cn } from '../lib/utils';

type IconSource = 'svg' | 'material-symbols';

type ColorStyle = { type: 'solid'; color: string } | { type: 'gradient'; colors: [string, string]; angle?: number };

type AppIconProps = {
  /** Size in pixels (always square) */
  size: number;
  /** Source of the icon: 'svg' for local SVG or 'material-symbols' for Google Material Symbols */
  source: IconSource;
  /** Name of the icon (filename without extension for SVG, slug for Material Symbols) */
  name: string;
  /** Color style: solid or linear gradient */
  colorStyle?: ColorStyle;
  /** Font weight (for Material Symbols) or stroke width multiplier (for SVG) */
  weight?: number;
  /** Additional CSS class */
  className?: string;
  /** Raw SVG content (required when source is 'svg') */
  svgContent?: string;
  /** Fill mode for Material Symbols (0 = outlined, 1 = filled) */
  fill?: 0 | 1;
  /** Optical size for Material Symbols */
  opticalSize?: number;
  /** Grade for Material Symbols */
  grade?: number;
  /** Additional inline styles */
  style?: React.CSSProperties;
};

/**
 * Generates a unique ID for SVG gradient definitions
 */
function generateGradientId(): string {
  return `gradient-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Modifies SVG string to apply size
 */
function svgWithSize(svg: string, size: number): string {
  let sizedSvg = svg.replace(/\swidth="[^"]*"/, ` width="${size}"`).replace(/\sheight="[^"]*"/, ` height="${size}"`);

  // Add a class containing "size-" to prevent button styles from forcing size-4.
  if (/<svg[^>]*class=/.test(sizedSvg)) {
    sizedSvg = sizedSvg.replace(/<svg([^>]*?)class="([^"]*)"/, (match, before, classValue) => {
      if (classValue.includes('size-')) {
        return match;
      }
      return `<svg${before}class="${classValue} size-guard"`;
    });
  } else {
    sizedSvg = sizedSvg.replace(/<svg([^>]*)>/, '<svg$1 class="size-guard">');
  }

  return sizedSvg;
}

/**
 * Calculates stroke width based on weight
 * Maps common font weights to stroke widths:
 * 400 (normal) -> 1.5
 * 600 (semi-bold) -> 2
 * 700 (bold) -> 2.5
 */
function weightToStrokeWidth(weight: number): number {
  // Linear interpolation from weight to stroke width
  // 400 -> 1.5, 600 -> 2.0, 700 -> 2.5
  const minWeight = 400;
  const maxWeight = 700;
  const minStroke = 1.5;
  const maxStroke = 2.5;

  const clampedWeight = Math.max(minWeight, Math.min(maxWeight, weight));
  const ratio = (clampedWeight - minWeight) / (maxWeight - minWeight);
  return minStroke + ratio * (maxStroke - minStroke);
}

/**
 * Modifies SVG string to apply stroke width based on weight
 */
function svgWithStrokeWidth(svg: string, weight: number): string {
  const strokeWidth = weightToStrokeWidth(weight);
  return svg
    .replaceAll(/stroke-width="[^"]*"/g, `stroke-width="${strokeWidth}"`)
    .replaceAll(/stroke-width='[^']*'/g, `stroke-width='${strokeWidth}'`);
}

/**
 * Modifies SVG to use gradient for stroke and fill
 */
function svgWithGradient(svg: string, gradientId: string, colors: [string, string], angle: number = 90): string {
  // Calculate gradient coordinates based on angle
  const angleRad = (angle * Math.PI) / 180;
  const x1 = 50 - Math.cos(angleRad) * 50;
  const y1 = 50 + Math.sin(angleRad) * 50;
  const x2 = 50 + Math.cos(angleRad) * 50;
  const y2 = 50 - Math.sin(angleRad) * 50;

  const gradientDef = `
    <defs>
      <linearGradient id="${gradientId}" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">
        <stop offset="0%" stop-color="${colors[0]}" />
        <stop offset="100%" stop-color="${colors[1]}" />
      </linearGradient>
    </defs>
  `;

  // Insert gradient definition after opening svg tag
  let modifiedSvg = svg.replace(/<svg([^>]*)>/, `<svg$1>${gradientDef}`);

  // Replace stroke="currentColor" with gradient reference
  modifiedSvg = modifiedSvg.replaceAll(/stroke="currentColor"/g, `stroke="url(#${gradientId})"`);
  modifiedSvg = modifiedSvg.replaceAll(/stroke='currentColor'/g, `stroke='url(#${gradientId})'`);

  // Replace fill="currentColor" with gradient reference (if any)
  modifiedSvg = modifiedSvg.replaceAll(/fill="currentColor"/g, `fill="url(#${gradientId})"`);
  modifiedSvg = modifiedSvg.replaceAll(/fill='currentColor'/g, `fill='url(#${gradientId})'`);

  return modifiedSvg;
}

/**
 * SVG Icon subcomponent
 */
function SvgIcon({
  svgContent,
  size,
  weight,
  colorStyle,
  className,
  customStyle,
}: {
  svgContent: string;
  size: number;
  weight: number;
  colorStyle?: ColorStyle;
  className?: string;
  customStyle?: React.CSSProperties;
}) {
  const gradientId = React.useMemo(() => {
    if (colorStyle?.type === 'gradient') {
      return generateGradientId();
    }
    return '';
  }, [colorStyle?.type]);

  let processedSvg = svgWithSize(svgContent, size);
  processedSvg = svgWithStrokeWidth(processedSvg, weight);

  if (colorStyle?.type === 'gradient') {
    processedSvg = svgWithGradient(processedSvg, gradientId, colorStyle.colors, colorStyle.angle);
  }

  const style: React.CSSProperties = {
    display: 'inline-flex',
    lineHeight: 0,
    color: colorStyle?.type === 'solid' ? colorStyle.color : 'inherit',
    ...customStyle,
  };

  return (
    <span aria-hidden="true" className={className} style={style} dangerouslySetInnerHTML={{ __html: processedSvg }} />
  );
}

/**
 * Material Symbols Icon subcomponent
 */
function MaterialSymbolsIcon({
  name,
  size,
  weight,
  colorStyle,
  className,
  fill,
  opticalSize,
  grade,
}: {
  name: string;
  size: number;
  weight: number;
  colorStyle?: ColorStyle;
  className?: string;
  fill: 0 | 1;
  opticalSize: number;
  grade: number;
}) {
  const gradientId = React.useMemo(() => {
    if (colorStyle?.type === 'gradient') {
      return generateGradientId();
    }
    return '';
  }, [colorStyle?.type]);

  const style: React.CSSProperties = {
    fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'opsz' ${opticalSize}, 'GRAD' ${grade}`,
    fontSize: `${size}px`,
  };

  if (colorStyle?.type === 'solid') {
    style.color = colorStyle.color;
  } else if (colorStyle?.type === 'gradient') {
    // For gradient, we need to use background-clip technique
    const angle = colorStyle.angle ?? 90;
    style.background = `linear-gradient(${angle}deg, ${colorStyle.colors[0]}, ${colorStyle.colors[1]})`;
    style.WebkitBackgroundClip = 'text';
    style.backgroundClip = 'text';
    style.WebkitTextFillColor = 'transparent';
  }

  return (
    <>
      {colorStyle?.type === 'gradient' && (
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={colorStyle.colors[0]} />
              <stop offset="100%" stopColor={colorStyle.colors[1]} />
            </linearGradient>
          </defs>
        </svg>
      )}
      <span className={cn('material-symbols-outlined select-none', className)} style={style}>
        {name}
      </span>
    </>
  );
}

/**
 * Unified Icon component that supports both local SVG icons and Google Material Symbols.
 *
 * @example
 * // SVG icon with solid color
 * <AppIcon
 *   source="svg"
 *   name="stacked-bar-chart"
 *   svgContent={stackedBarChartSvg}
 *   size={20}
 *   weight={600}
 *   colorStyle={{ type: 'solid', color: '#ffffff' }}
 * />
 *
 * @example
 * // Material Symbols icon with gradient
 * <AppIcon
 *   source="material-symbols"
 *   name="corporate_fare"
 *   size={24}
 *   weight={400}
 *   colorStyle={{ type: 'gradient', colors: ['#ff0000', '#0000ff'], angle: 45 }}
 * />
 */
export function AppIcon({
  size,
  source,
  name,
  colorStyle,
  style,
  weight = 400,
  className,
  svgContent,
  fill = 0,
  opticalSize = 24,
  grade = 0,
}: AppIconProps) {
  if (source === 'svg') {
    if (!svgContent) {
      // svgContent is required when source is 'svg'
      return null;
    }

    return (
      <SvgIcon
        svgContent={svgContent}
        size={size}
        weight={weight}
        colorStyle={colorStyle}
        className={className}
        customStyle={style}
      />
    );
  }

  // Material Symbols
  return (
    <MaterialSymbolsIcon
      name={name}
      size={size}
      weight={weight}
      colorStyle={colorStyle}
      className={className}
      fill={fill}
      opticalSize={opticalSize}
      grade={grade}
    />
  );
}

export default AppIcon;
